/**
 * Ensures that at most one promise is running at a time. Calls the given
 * callback before and after a consecutive chain of promises has been called.
 * That means, running one promise through the chunker calls the callback once
 * before the first promise is executed. If - before this promise has finished -
 * another promise is added, then the callback is called only after this second
 * promise has finished. See the following example scheme:
 *
 * queue    promise1
 * callback start
 * start    promise1
 * end      promise1
 * callback end
 *
 * queue    promise2
 * callback start
 * start    promise2
 * queue    promise3
 * end      promise2
 * start    promise3
 * end      promise3
 * callback end
 *
 * @param {function} cb - Callback function
 * @returns {chunker} Chunker function
 */
export default function createPromiseChunker(cb) {
  let current = null

  function notify(transition, promise, err, res) {
    if (transition === 'start') {
      cb('start', undefined, undefined) // eslint-disable-line standard/no-callback-literal
    }

    if (transition === 'end' && current === promise) {
      cb('end', err, res) // eslint-disable-line standard/no-callback-literal
      current = null
    }
  }

  return function run(promiseCreator) {
    if (!current) {
      notify('start', current, undefined, undefined)
      current = Promise.resolve().then(() => promiseCreator())
    } else {
      current = current.then(() => promiseCreator())
    }

    const temp = current
    temp.then((res) => notify('end', temp, undefined, res)).catch((err) => notify('end', temp, err, undefined))
    return temp
  }
}
